import { graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';

import useMemberInfo, { IFragment } from '@hooks/useMemberInfo';

import { keysToCamel } from '@utils/butter';

import { IAuthenticatedMemberWithUnscheduledAppointments } from '@folx-types/member';
import { IMemberAppointments } from '@folx-types/member';

import {
	appointmentsName,
	appointments as appointmentsQuery,
} from '@graphql/fragments/appointments';
import { unscheduledAppointments as UNSCHEDULED_APPOINTMENTS } from '@graphql/fragments/unscheduledAppointments';

import RescheduleAppointment, {
	IRescheduleAppointContent,
} from './RescheduleAppointment.component';

interface IRescheduleAppointmentPageProps {
	appointmentId: string;
}

const RescheduleAppointmentPage: FC<IRescheduleAppointmentPageProps> = ({
	appointmentId,
	...props
}) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "member-appointment-selection" }) {
				cancel_header
				reschedule_header
				appointment_breadcrumb
				appointment_disclaimer
				appointment_subheader
				appointment_loading
				confirmation_header
				confirmation_description
				cancellation_header
				cancellation_description
				past_membership_cancel_date {
					header
					subheader
					description
					cta
					secondary_cta
				}
				error_messages {
					contact_to_reschedule
				}
			}
		}
	`);

	const appointmentsFragment: IFragment = {
		name: appointmentsName,
		node: appointmentsQuery,
	};
	const unscheduledAppointmentsFragment: IFragment = {
		name: 'UnscheduledAppointments',
		node: UNSCHEDULED_APPOINTMENTS,
	};

	const { loading: memberLoading, memberInfo } = useMemberInfo<
		IAuthenticatedMemberWithUnscheduledAppointments & IMemberAppointments
	>([unscheduledAppointmentsFragment, appointmentsFragment]);

	const unscheduledAppointments = memberInfo?.unscheduledAppointments ?? [];
	const appointments = memberInfo?.appointments ?? [];
	const selectedAppointment = [...unscheduledAppointments, ...appointments]?.find(
		(appt) => appt.cogsworthId === appointmentId
	);

	const refinedContent: IRescheduleAppointContent = keysToCamel(
		pageContent
	) as IRescheduleAppointContent;

	return (
		<RescheduleAppointment
			selectedAppointment={selectedAppointment}
			loading={memberLoading}
			pageContent={refinedContent}
			rescheduled={props?.location?.state?.rescheduled}
			isPastCancelledDate={props?.location?.state?.isPastCancelledDate}
			apptDate={props?.location?.state?.apptDate}
			memberInfo={memberInfo}
		/>
	);
};

export default RescheduleAppointmentPage;
